import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { Grid, Flex, Box, Text, Heading, Card, Skeleton } from '@pancakeswap/uikit'
import { fromUnixTime } from 'date-fns'
import { useTranslation } from 'contexts/Localization'
import Page from 'components/Layout/Page'
import LineChart from 'views/Info/components/InfoCharts/LineChart'
import TokenTable from 'views/Info/components/InfoTables/TokensTable'
import PoolTable from 'views/Info/components/InfoTables/PoolsTable'
import { formatAmount } from 'utils/formatInfoNumbers'
import BarChart from 'views/Info/components/InfoCharts/BarChart'
import {
  useAllPoolData,
  useAllTokenData,
  useProtocolChartData,
  useProtocolData,
  useProtocolTransactions,
} from 'state/info/hooks'
import TransactionTable from 'views/Info/components/InfoTables/TransactionsTable'

import { isSecurityToken, restrictedTokens } from 'config/constants/tokens'

export const ChartCardsContainer = styled(Flex)`
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  padding: 0;
  gap: 1em;

  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  } ;
`

export const DexStyledBanner = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  row-gap: 8px;
  height: 204px;
  padding: 24px;
  border-radius: 8px;
  background-image: ${(props: { banner: string }) => `url("/images/banner-${props.banner}.png")`};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  p {
    width: 55%;
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: #ffffff;
  }

  a {
    margin-top: 16px;
    padding: 10px 50px;
    background: #46b9ff;
    box-shadow: 0px 1px 1px rgba(32, 36, 46, 0.12), 0px 2px 8px rgba(70, 185, 255, 0.24);
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #ffffff;
  }
`

const Overview: React.FC = () => {
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const [liquidityHover, setLiquidityHover] = useState<number | undefined>()
  const [liquidityDateHover, setLiquidityDateHover] = useState<string | undefined>()
  const [volumeHover, setVolumeHover] = useState<number | undefined>()
  const [volumeDateHover, setVolumeDateHover] = useState<string | undefined>()

  const [protocolData] = useProtocolData()
  const [chartData] = useProtocolChartData()
  const [transactions] = useProtocolTransactions()

  const currentDate = new Date().toLocaleString(locale, { month: 'short', year: 'numeric', day: 'numeric' })

  // Getting latest liquidity and volumeUSD to display on top of chart when not hovered
  useEffect(() => {
    if (volumeHover == null && protocolData) {
      setVolumeHover(protocolData.volumeUSD)
    }
  }, [protocolData, volumeHover])
  useEffect(() => {
    if (liquidityHover == null && protocolData) {
      setLiquidityHover(protocolData.liquidityUSD)
    }
  }, [liquidityHover, protocolData])

  const formattedLiquidityData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: fromUnixTime(day.date),
          value: day.liquidityUSD,
        }
      })
    }
    return []
  }, [chartData])

  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: fromUnixTime(day.date),
          value: day.volumeUSD,
        }
      })
    }
    return []
  }, [chartData])

  const allTokens = useAllTokenData()

  const formattedTokens = useMemo(() => {
    return Object.values(allTokens)
      .map((token) => token.data)
      .filter((token) => token)
  }, [allTokens])
    .filter((token) => !restrictedTokens.includes(token.address.toLowerCase()))
    .filter((token) => token.liquidityToken >= 3)

  const digitalTokens = formattedTokens.filter((token) => {
    return !isSecurityToken(token.address)
  })

  const securityTokens = formattedTokens.filter((token) => {
    return isSecurityToken(token.address)
  })

  const allPoolData = useAllPoolData()

  const poolDatas = useMemo(() => {
    return Object.values(allPoolData)
      .map((pool) => pool.data)
      .filter((pool) => pool)
  }, [allPoolData])

  const digitalPools = []
  const securityPools = []

  poolDatas.forEach((pool) => {
    if (pool.liquidityUSD < 500.0) return
    if (isSecurityToken(pool.token0.address) || isSecurityToken(pool.token1.address)) {
      securityPools.push(pool)
    } else {
      digitalPools.push(pool)
    }
  })

  const somePoolsAreLoading = useMemo(() => {
    return Object.values(allPoolData).some((pool) => !pool.data)
  }, [allPoolData])

  return (
    <Page>
      <Heading scale="xl" lineHeight="40px" textAlign="center" mb="8px" id="info-overview-title">
        {t('Secondary market for security tokens')}
      </Heading>
      <Text textAlign="center" color="textDescription" mb="40px">
        {t('Oveview of tokens, pools & transaction history')}
      </Text>
      <ChartCardsContainer>
        <Card>
          <Box p={['16px', '16px', '24px']}>
            <Text bold color="secondary">
              {t('Liquidity')}
            </Text>
            {liquidityHover > 0 ? (
              <Text bold fontSize="24px">
                ${formatAmount(liquidityHover)}
              </Text>
            ) : (
              <Skeleton width="128px" height="36px" />
            )}
            <Text>{liquidityDateHover ?? currentDate}</Text>
            <Box height="250px">
              <LineChart
                data={formattedLiquidityData}
                setHoverValue={setLiquidityHover}
                setHoverDate={setLiquidityDateHover}
              />
            </Box>
          </Box>
        </Card>
        <Card>
          <Box p={['16px', '16px', '24px']}>
            <Text bold color="secondary">
              {t('Volume 24H')}
            </Text>
            {volumeHover > 0 ? (
              <Text bold fontSize="24px">
                ${formatAmount(volumeHover)}
              </Text>
            ) : (
              <Skeleton width="128px" height="36px" />
            )}
            <Text>{volumeDateHover ?? currentDate}</Text>
            <Box height="250px">
              <BarChart data={formattedVolumeData} setHoverValue={setVolumeHover} setHoverDate={setVolumeDateHover} />
            </Box>
          </Box>
        </Card>
      </ChartCardsContainer>

      <Heading scale="lg" mt="40px" mb="16px">
        {t('Security Tokens')}
      </Heading>
      <TokenTable tokenDatas={securityTokens} />

      <Heading scale="lg" mt="40px" mb="16px">
        {t('Security Tokens Pools')}
      </Heading>

      <PoolTable poolDatas={securityPools} loading={somePoolsAreLoading} />

      <Flex flexDirection="column">
        <Grid
          mt="34px"
          gridTemplateColumns={['1fr', null, null, 'repeat(2, 1fr)']}
          gridColumnGap="24px"
          gridRowGap="24px"
        >
          <DexStyledBanner href="https://www.stobox.io/tokenization" target="_blank" banner="3">
            <Text color="backgroundAlt">For businesses</Text>
            <Heading scale="lg" lineHeight="32px" color="backgroundAlt" maxWidth="319px">
              Tokenization of Real World Assets with Stobox
            </Heading>
          </DexStyledBanner>
          <DexStyledBanner href="https://www.stobox.io/stbx" target="_blank" banner="4">
            <Text color="backgroundAlt">For investors</Text>
            <Heading scale="lg" color="backgroundAlt" lineHeight="32px" maxWidth="319px">
              Share long-term growth by investing in Stobox
            </Heading>
          </DexStyledBanner>
        </Grid>
      </Flex>

      <Heading scale="lg" mt="40px" mb="16px">
        {t('Digital Assets')}
      </Heading>
      <TokenTable tokenDatas={digitalTokens} />

      <Heading scale="lg" mt="40px" mb="16px">
        {t('Digital Assets Pools')}
      </Heading>
      <PoolTable poolDatas={digitalPools} loading={somePoolsAreLoading} />

      <Heading scale="lg" mt="40px" mb="16px">
        {t('Transactions')}
      </Heading>
      <TransactionTable transactions={transactions} />
    </Page>
  )
}

export default Overview
